/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
    showCreateListingsLink,
  } = props;

  const user = ensureCurrentUser(currentUser);

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  const farmiezFarmer = currentUser?.attributes?.profile?.publicData?.userType == "farmiez-farmer"
  const farmiezShopper = currentUser?.attributes?.profile?.publicData?.userType == "farmiez-shopper"

  const createListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink className={css.createNewListingLink} name="NewListingPage">
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </NamedLink>
  ) : null;

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <ul className={css.pagesLinkWrapper}>
            <li>
              <NamedLink name="HowItWorksPage" className={css.coopButton}>How It Works</NamedLink>
            </li>
            <li>
              <NamedLink name="SubscriptionPlansPage" className={css.coopButton}>Plan & Pricing</NamedLink>
            </li>
            {farmiezFarmer && (
              <li>
                <NamedLink name="SignupPage" className={css.coopButton}>
                  <span>🐓</span> 
                  <span>Join the Coop (Farmer)</span>
                </NamedLink>
              </li>
            )}
            {farmiezShopper && (
              <li>
                <NamedLink name="SignupPage" className={css.coopButton}>
                  <span>🥚</span> 
                  <span>Shop Farmiez Now (Shopper)</span>
                </NamedLink>
              </li>
            )}
            <li>
              <NamedLink name="FAQPage" className={css.coopButton}>FAQ</NamedLink>
            </li>
            <li>
              <NamedLink name="AboutUsPage" className={css.coopButton}>About Us</NamedLink>
            </li>
            <li>
              <NamedLink name="OurStoryPage" className={css.coopButton}>Our Story</NamedLink>
            </li>
            <li>
              <NamedLink name="FarmiezFieldGuidesPage" className={css.coopButton}>Farmiez Field Guides</NamedLink>
            </li>
            <li>
              <NamedLink name="PrivacyPolicyPage" className={css.coopButton}>Privacy Policy</NamedLink>
            </li>
            <li>
              <NamedLink name="TermsOfServicePage" className={css.coopButton}>Terms of Service</NamedLink>
            </li>
          </ul>

          <div className={css.customLinksWrapper}>{extraLinks}</div>

          <div className={css.spacer} />
        </div>
        <div className={css.footer}>{createListingsLinkMaybe}</div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  const manageListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink
      className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
      name="ManageListingsPage"
    >
      <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
    </NamedLink>
  ) : null;

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <div className={css.accountLinksWrapper}>
          <NamedLink
            className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink>
          {manageListingsLinkMaybe}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>

          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('VendorDashboardPage'))}
            name="VendorDashboardPage"
          >
            Vendor Dashboard
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ShopperDashboardPage'))}
            name="ShopperDashboardPage"
          >
            Shopper Dashboard
          </NamedLink>
        </div>
        <ul className={css.pagesLinkWrapper}>
          <li>
            <NamedLink name="FAQPage" className={css.coopButton}>FAQ</NamedLink>
          </li>
          <li>
            <NamedLink name="AboutUsPage" className={css.coopButton}>About Us</NamedLink>
          </li>
          <li>
            <NamedLink name="OurStoryPage" className={css.coopButton}>Our Story</NamedLink>
          </li>
          <li>
            <NamedLink name="FarmiezFieldGuidesPage" className={css.coopButton}>Farmiez Field Guides</NamedLink>
          </li>
          <li>
            <NamedLink name="HowItWorksPage" className={css.coopButton}>How It Works</NamedLink>
          </li>
          <li>
            <NamedLink name="PrivacyPolicyPage" className={css.coopButton}>Privacy Policy</NamedLink>
          </li>
          <li>
            <NamedLink name="TermsOfServicePage" className={css.coopButton}>Terms of Service</NamedLink>
          </li>
          {farmiezFarmer && (
            <li>
              <NamedLink name="SignupPage" className={css.coopButton}>
                <span>🐓</span> 
                <span>Join the Coop (Farmer)</span>
              </NamedLink>
            </li>
          )}
          {farmiezShopper && (
            <li>
              <NamedLink name="SignupPage" className={css.coopButton}>
                <span>🥚</span> 
                <span>Shop Farmiez Now (Shopper)</span>
              </NamedLink>
            </li>
          )}
        </ul>

        <div className={css.customLinksWrapper}>{extraLinks}</div>
        <div className={css.spacer} />
      </div>
      <div className={css.footer}>{createListingsLinkMaybe}</div>
    </div>
  );
};

export default TopbarMobileMenu;
